import ApiClient, { createTokenConfig } from 'api/ApiClient';
import {
    IdogcamCamera,
    IdogcamCameraChanges,
    IdogcamCamerasPage,
    IdogcamCredentials,
    IdogcamIntegration,
    IdogcamRoom,
    IdogcamViewerLaunch
} from '../model/IdogcamModel';
import {
    convertCamera,
    convertCameraChangesToDto,
    convertCamerasPage,
    convertIntegration,
    convertRoom,
    convertViewerLaunch
} from './IdogcamConverter';
import {
    IdogcamCameraDto,
    IdogcamCamerasPageDto,
    IdogcamIntegrationDto,
    IdogcamIntegrationUpdateDto,
    IdogcamRoomsPageDto,
    IdogcamViewerLaunchDto
} from './IdogcamDtos';

// Rooms are few per location; one large page is enough to fill the Room / Unit selects.
const ROOMS_PAGE_SIZE = 500;

// Every iDogCam endpoint is scoped to the marketplace (location) sent as marketplace_id.
const marketplaceParams = (marketplaceId: number) => ({ marketplace_id: marketplaceId });

export interface FetchIdogcamCamerasParams {
    marketplaceId: number;
    token: string;
    page: number;
    size: number;
    query?: string;
}

export const fetchIdogcamIntegration = async (marketplaceId: number, token: string): Promise<IdogcamIntegration> => {
    const { data } = await ApiClient.get<IdogcamIntegrationDto>('idogcam/integration', {
        ...createTokenConfig(token),
        params: marketplaceParams(marketplaceId)
    });
    return convertIntegration(data);
};

// Empty values remove the stored setting (used to roll back a failed connection).
export const updateIdogcamIntegration = async (
    marketplaceId: number,
    token: string,
    credentials: IdogcamCredentials
): Promise<IdogcamIntegration> => {
    const body: IdogcamIntegrationUpdateDto = { kennel_id: credentials.kennelId, erpcode: credentials.erpcode };
    const { data } = await ApiClient.patch<IdogcamIntegrationDto>(
        'idogcam/integration',
        { integration: body },
        { ...createTokenConfig(token), params: marketplaceParams(marketplaceId) }
    );
    return convertIntegration(data);
};

export const fetchIdogcamCameras = async ({
    marketplaceId,
    token,
    page,
    size,
    query
}: FetchIdogcamCamerasParams): Promise<IdogcamCamerasPage> => {
    const { data } = await ApiClient.get<IdogcamCamerasPageDto>('idogcam/cameras', {
        ...createTokenConfig(token),
        params: { ...marketplaceParams(marketplaceId), page, size, query: query?.trim() || undefined }
    });
    return convertCamerasPage(data);
};

// Pulls the camera list from iDogCam into Cuddles. Temporary until the backend syncs on a schedule.
export const syncIdogcamCameras = async (marketplaceId: number, token: string): Promise<void> => {
    await ApiClient.post('idogcam/cameras/sync', null, {
        ...createTokenConfig(token),
        params: marketplaceParams(marketplaceId)
    });
};

export const updateIdogcamCamera = async (
    marketplaceId: number,
    token: string,
    cameraId: number,
    changes: IdogcamCameraChanges
): Promise<IdogcamCamera> => {
    const { data } = await ApiClient.patch<IdogcamCameraDto>(
        `idogcam/cameras/${cameraId}`,
        { camera: convertCameraChangesToDto(changes) },
        { ...createTokenConfig(token), params: marketplaceParams(marketplaceId) }
    );
    return convertCamera(data);
};

export const fetchIdogcamViewerLaunch = async (
    marketplaceId: number,
    token: string,
    cameraId: number
): Promise<IdogcamViewerLaunch> => {
    const { data } = await ApiClient.post<IdogcamViewerLaunchDto>(`idogcam/cameras/${cameraId}/viewer`, null, {
        ...createTokenConfig(token),
        params: marketplaceParams(marketplaceId)
    });
    return convertViewerLaunch(data);
};

export const fetchIdogcamRooms = async (marketplaceId: number, token: string): Promise<Array<IdogcamRoom>> => {
    const { data } = await ApiClient.get<IdogcamRoomsPageDto>('rooms', {
        ...createTokenConfig(token),
        params: { ...marketplaceParams(marketplaceId), page: 1, size: ROOMS_PAGE_SIZE }
    });
    return data.rooms.map(convertRoom);
};
